import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as createGuid } from 'uuid';

import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { useId } from 'src/components/Router/useId';
import { EDIT, ADD } from 'src/consts';
import { certsyLinkClicked } from 'src/modules/analytics/certsy/actionCreators';
import { useAppConfig } from 'src/modules/appConfig';
import type { ActionName } from 'src/types';
import * as Schema from 'src/types/graphql';

import {
  useAddUnconfirmedQualification,
  useDeleteQualification,
  useGetConfirmedQualifications,
  useGetUnconfirmedQualifications,
  useRejectUnconfirmedQualification,
  useUpdateQualification,
  useUpdateVerifiedQualification,
} from '../data';
import type { FormValues } from '../types';
import {
  qualificationFormOrEducationUpdateToConfirmQualificationInput,
  qualificationFormUpdateToUpdateQualificationInput,
} from '../utils';

import { EducationFormRenderer } from './EducationFormRenderer';

const credentialType = 'Qualification';

export const EditEducation = ({ onFormDirtyChange }: DirtyFormProps) => {
  const dispatch = useDispatch();
  const enterReadMode = useEnterReadMode();
  const { language: languageCode, zone } = useAppConfig();

  const { confirmedQualifications, loading: confirmedQualificationsLoading } =
    useGetConfirmedQualifications();
  const {
    unconfirmedQualifications,
    loading: unconfirmedQualificationsLoading,
  } = useGetUnconfirmedQualifications();

  const paramId = useId();
  const activeQualification = [
    ...confirmedQualifications,
    ...unconfirmedQualifications,
  ].find((qualification) => qualification.id === paramId);
  const activeQualificationId = activeQualification?.id;

  const [actionName] = useState<ActionName>(activeQualification ? EDIT : ADD);

  const isVerifiedQualification = Boolean(activeQualification?.credential);

  const unconfirmedQualification = unconfirmedQualifications.find(
    (qualification) => qualification.id === paramId,
  );

  const updateQualification = useUpdateQualification(actionName);
  const [updateVerifiedQualification] = useUpdateVerifiedQualification();
  const deleteQualification = useDeleteQualification();
  const [addUnconfirmedQualificationMutation] =
    useAddUnconfirmedQualification(EDIT);
  const [rejectUnconfirmedQualification] = useRejectUnconfirmedQualification();

  const [updateMutation] = updateQualification;

  const [deleteQualificationMutation] = deleteQualification;

  const submitForm = useCallback(
    ({ dirty, values }: { dirty: boolean; values: FormValues }): void => {
      if (unconfirmedQualification) {
        addUnconfirmedQualificationMutation({
          qualification:
            qualificationFormOrEducationUpdateToConfirmQualificationInput({
              ...values,
              tracking: unconfirmedQualification.tracking,
            }),
          languageCode,
          zone,
          isUnifiedSite: true,
        });
      } else if (dirty) {
        const qualificationId = activeQualificationId || createGuid();
        const qualificationData =
          qualificationFormUpdateToUpdateQualificationInput({
            ...values,
            id: qualificationId,
          });

        updateMutation({
          qualification: qualificationData,
          languageCode,
          zone,
          isUnifiedSite: true,
        });
      }
      enterReadMode();
    },
    [
      activeQualificationId,
      addUnconfirmedQualificationMutation,
      enterReadMode,
      languageCode,
      unconfirmedQualification,
      updateMutation,
      zone,
    ],
  );

  const deleteEducation = () => {
    if (unconfirmedQualification) {
      rejectUnconfirmedQualification({
        qualification: {
          id: unconfirmedQualification.id,
          dataType: Schema.UnconfirmedDataType.Education,
          tracking: unconfirmedQualification.tracking || null,
        },
        languageCode,
        zone,
        isUnifiedSite: true,
      });
    } else if (activeQualificationId) {
      deleteQualificationMutation({
        qualificationId: { id: activeQualificationId },
        languageCode,
        zone,
        isUnifiedSite: true,
      });
    }
    enterReadMode();
  };

  const onCertsyDeleteOpen = useCallback(() => {
    dispatch(
      certsyLinkClicked({
        linkUrl: activeQualification?.credential?.deleteVerificationUrl,
        linkAction: 'delete',
        linkPosition: 'pop up',
        credentialType,
        currentPageSection: 'education',
      }),
    );
    window.open(
      activeQualification?.credential?.deleteVerificationUrl,
      '_blank',
      'noopener,noreferrer',
    );
  }, [activeQualification, dispatch]);

  const submitVerifiedForm = useCallback(
    (params: { dirty: boolean; values: { highlights: string } }): void => {
      if (activeQualification) {
        const { dirty, values } = params;
        if (dirty) {
          updateVerifiedQualification({
            verifiedQualificationData: {
              ...values,
              id: activeQualification.id,
            },
            languageCode,
            zone,
            isUnifiedSite: true,
          });
        }
        enterReadMode();
      }
    },
    [
      activeQualification,
      enterReadMode,
      languageCode,
      updateVerifiedQualification,
      zone,
    ],
  );

  const onManageClick = useCallback(() => {
    dispatch(
      certsyLinkClicked({
        linkUrl: activeQualification?.credential?.manageVerificationUrl,
        linkAction: 'manage',
        linkPosition: 'main card',
        credentialType,
        currentPageSection: 'education',
      }),
    );
  }, [activeQualification, dispatch]);

  const onCertsyClick = useCallback(() => {
    dispatch(
      certsyLinkClicked({
        linkUrl: activeQualification?.credential?.manageVerificationUrl,
        linkAction: 'manage',
        linkPosition: 'pop up',
        credentialType,
        currentPageSection: 'education',
      }),
    );
  }, [activeQualification, dispatch]);

  const allowDelete = actionName === EDIT;

  const onDelete = isVerifiedQualification
    ? onCertsyDeleteOpen
    : deleteEducation;

  if (confirmedQualificationsLoading || unconfirmedQualificationsLoading) {
    return null;
  }

  return (
    <EducationFormRenderer
      isVerifiedEducation={isVerifiedQualification}
      activeEducation={activeQualification}
      submitForm={submitForm}
      submitVerifiedForm={submitVerifiedForm}
      onCancel={enterReadMode}
      actionName={actionName}
      onFormDirtyChange={onFormDirtyChange}
      validateOnMount={Boolean(unconfirmedQualification)}
      onManageClick={onManageClick}
      onCertsyClick={onCertsyClick}
      {...(allowDelete && { onDelete })}
    />
  );
};
